import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { CartItem } from '@/components/storefront/CartDrawer';
import type { Product } from '@/data/allProducts';

const CART_STORAGE_KEY = 'ziko_cart_v1';
const CART_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 1 month

interface StoredCart {
  items: CartItem[];
  savedAt: number;
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);

    // Pre-TTL format was just a plain array — treat as fresh rather than
    // discarding it (there's no savedAt to judge its age by).
    if (Array.isArray(parsed)) return parsed;

    const { items, savedAt } = parsed as StoredCart;
    if (!Array.isArray(items)) return [];
    if (typeof savedAt === 'number' && Date.now() - savedAt > CART_TTL_MS) {
      // Abandoned for over a month — prices or stock may have changed
      // since, so don't resurrect it silently.
      return [];
    }
    return items;
  } catch {
    return [];
  }
}

interface CartContextValue {
  cart: CartItem[];
  cartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  /** Simple add — merges by product id, +1 quantity. What most product cards use. */
  addToCart: (product: Product) => void;
  /** Low-level add for callers that build their own composite id (e.g. a
   * product page merging in the selected variant/pack/sub-options). */
  addItem: (item: CartItem) => void;
  updateQty: (id: string, qty: number) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

/**
 * Cart state used to live as separate `useState` in every single page
 * (Index, ProductDetail, BestSellers, ProductsCatalogue, GiftCards, etc.),
 * which meant it reset on every in-app navigation, not just a hard refresh.
 * This provider lifts it to one shared place and persists it to
 * localStorage so it survives both.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(loadCart);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    try {
      const payload: StoredCart = { items: cart, savedAt: Date.now() };
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Storage full or unavailable (e.g. private browsing) — the cart
      // still works for the rest of this session, it just won't survive
      // a refresh. Not worth surfacing to the user.
    }
  }, [cart]);

  const addItem = useCallback((item: CartItem) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i);
      }
      return [...prev, item];
    });
  }, []);

  const addToCart = useCallback((product: Product) => {
    addItem({ ...product, quantity: 1 });
  }, [addItem]);

  const updateQty = useCallback((id: string, qty: number) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(i => i.id !== id));
      return;
    }
    setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));
  }, []);

  const removeItem = useCallback((id: string) => {
    setCart(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);
  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);

  return (
    <CartContext.Provider value={{ cart, cartOpen, openCart, closeCart, addToCart, addItem, updateQty, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart() must be used inside <CartProvider>');
  return ctx;
}

